import {Injectable, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AppState, selectConfiguration, selectMicrositeSessionsIsLoggedIn, track} from '../../core.state';

import {Store} from '@ngrx/store';
import {catchError, exhaustMap, map, takeUntil, withLatestFrom} from "rxjs/operators";

import {EMPTY, of, Subject} from "rxjs";
import {
  createComment,
  deleteComment,
  failCreateComment,
  failLoadComments,
  failUpdateDeleteComment,
  loadComments,
  successComments,
  successCreateComment,
  updateComment,
} from "../syndicates.actions";
import {selectSyndicatesState} from "../syndicates.selectors";
import {CommentaryClient} from '../../../client/commentary';


@Injectable()
export class SyndicateCommentaryEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();


  loadComments = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadComments),
      withLatestFrom(this.store.select(selectSyndicatesState), this.store.select(selectMicrositeSessionsIsLoggedIn), this.store.select(selectConfiguration)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, syndicatesState, isLoggedIn, config]) => {
        if (!isLoggedIn) return EMPTY;
        if (!config.commentarySettings) return EMPTY;
        return this._commentaryClient.get(syndicatesState.commentaryParams).pipe(
          map((data: any) => {
            return successComments({response: data})
          }),
          catchError(error => {
            console.error('[Commentary Effects] Failed to load comments:', error);
            return of(failLoadComments({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  createComment = createEffect(() => {
    return this.actions$.pipe(
      ofType(createComment),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate comment creation
      exhaustMap((propVal) => {
        return this._commentaryClient.create({post: propVal.model}).pipe(
          map((data: any) => {
            // Reload comments after successful creation
            this.store.dispatch(loadComments({params: {feed_type: 'syndicate', page: 1}}));
            return successCreateComment({response: data});
          }),
          catchError(error => {
            console.error('[Commentary Effects] Failed to create comment:', error);
            return of(failCreateComment({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  updateComment = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateComment),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate comment updates
      exhaustMap((propVal) => {
        return this._commentaryClient.update(propVal.model).pipe(
          map((data: any) => {
            // Reload comments after successful update
            this.store.dispatch(loadComments({params: {feed_type: 'syndicate', page: 1}}));
            return successCreateComment({response: data});
          }),
          catchError(error => {
            console.error('[Commentary Effects] Failed to update comment:', error);
            return of(failUpdateDeleteComment({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  trackSuccessCreateComment = createEffect(() => {
    return this.actions$.pipe(
      ofType(successCreateComment),
      withLatestFrom(this.store.select(selectSyndicatesState)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, syndicateState]) => {
        const ID = syndicateState?.portion?.id || syndicateState.commentaryParams.model_id;
        const eventValue = {
          model_id: ID,
          model_name: 'syndicate'
        };
        const event = {
          eventCategory: 'comment',
          eventAction: 'microsite',
          eventLabel: 'create',
          eventValue: encodeURIComponent(JSON.stringify(eventValue))
        };
        return of(track({event: event}));
      })
    )
  }, {dispatch: true});

  deleteComment = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteComment),
      withLatestFrom(this.store.select(selectSyndicatesState)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, syndicateState]) => {
        return this._commentaryClient.delete(String(propVal.commentaryId)).pipe(
          map((data: any) => {
            return loadComments({params: Object.assign({}, syndicateState.commentaryParams, {page: 1})})
          }),
          catchError(error => {
            return of(failUpdateDeleteComment({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    private _commentaryClient: CommentaryClient
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
